import { useCallback } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";

/**
 * Fetcher function for categories - fetches from both databases
 */
const fetchCategoriesFromFirestore = async () => {
  // Fetch VCE categories
  const vceCategoriesSnapshot = await vceFirestore.collection("categories").get();
  const vceCategories = vceCategoriesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "VCE",
  }));

  // Fetch HVO categories
  const hvoCategoriesSnapshot = await hvoFirestore.collection("categories").get();
  const hvoCategories = hvoCategoriesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    shopSource: "HVO",
  }));

  return [...vceCategories, ...hvoCategories];
};

/**
 * Hook to fetch and manage categories from both VCE and HVO Firestore databases with SWR caching
 * @param {Object} options - Configuration options
 * @param {number} options.refreshInterval - Auto-refresh interval in ms (default: 0 - no auto refresh)
 * @returns {Object} - Categories data and utility functions
 */
export const useCategories = (options = {}) => {
  const { refreshInterval = 0 } = options;

  // Use SWR for data fetching with caching
  const { data: categories, error, isLoading, mutate } = useSWR(
    "categories",
    fetchCategoriesFromFirestore,
    {
      refreshInterval,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 10000, // Dedupe requests within 10 seconds
    }
  );

  /**
   * Manually refresh categories
   * @returns {Promise<Array>} - Updated categories
   */
  const refreshCategories = useCallback(() => {
    return mutate();
  }, [mutate]);

  /**
   * Get categories filtered by shop source
   * @param {string} shopSource - "VCE" or "HVO"
   * @returns {Array} - Filtered categories
   */
  const getCategoriesByShop = useCallback(
    (shopSource) => {
      if (!shopSource || !categories) return categories || [];
      return categories.filter((category) => category.shopSource === shopSource);
    },
    [categories],
  );

  /**
   * Find a specific category by ID
   * @param {string} categoryId - The category ID to search for
   * @returns {Object|null} - The category object or null if not found
   */
  const findCategoryById = useCallback(
    (categoryId) => {
      if (!categoryId || !categories) return null;
      return categories.find((category) => category.id === categoryId) || null;
    },
    [categories],
  );

  return {
    categories: categories || [],
    isLoading,
    error,
    refreshCategories,
    getCategoriesByShop,
    findCategoryById,
  };
};
